import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { pdfjs } from 'react-pdf';
import LearnApi from '@/api/learn';
import { IcertificateDetails } from '../../TypesAndInterfaces';
import {
  extractCertificateIds,
  getCertificatePdfUrl,
  hidePdfLayers,
} from '../utils/certificateUtils';

interface UseCertificatesProps {
  setCertificateDetails: React.Dispatch<React.SetStateAction<IcertificateDetails>>;
  isCareerPath: boolean;
}

interface UseCertificatesReturn {
  allCertificates: IcertificateDetails[];
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  isCertLoading: boolean;
  setIsCertLoading: (loading: boolean) => void;
  pageLoader: boolean;
  shareUrl: string;
  certificateImage: string;
}

const useCertificates = ({
  setCertificateDetails,
  isCareerPath,
}: UseCertificatesProps): UseCertificatesReturn => {
  const [allCertificates, setAllCertificates] = useState<IcertificateDetails[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isCertLoading, setIsCertLoading] = useState(false);
  const [pageLoader, setPageLoader] = useState(true);
  const [shareUrl, setShareUrl] = useState('');
  const [certificateImage, setCertificateImage] = useState('');
  const router = useRouter();

  // Fetch multiple certificates using Promise.allSettled
  const fetchAllCertificates = useCallback(async () => {
    try {
      setPageLoader(true);
      const ids = extractCertificateIds(router.query);

      if (ids.length === 0) {
        setPageLoader(false);
        return;
      }

      // Use allSettled to ignore failed promises and only process successful ones
      const responses = await Promise.allSettled(
        ids.map((certId) => LearnApi.getCertificateDetails<any>(certId, isCareerPath))
      );

      const certs: IcertificateDetails[] = responses
        .filter((res) => res.status === 'fulfilled' && res.value?.data)
        .map((res: any) => ({
          _id: res.value.data?._id,
          userDetails: {
            firstName: res.value.data?.firstName,
            lastName: res.value.data?.lastName,
            img: {
              link: res.value.data?.img?.link,
              source: res.value.data?.img?.source,
            },
          },
          certificate: {
            _id: res.value.data?._id,
            id: res.value.data?.id,
            dateCompleted: res.value.data?.dateCompleted,
            type: res.value.data?.certificateType || res.value.data?.courseName,
          },
          courseName: res.value.data?.courseName,
          isPartnerCertificate: res.value.data?.isPartnerCertificate,
          partnerName: res.value.data?.partnerName,
          certificateType: res.value.data?.certificateType,
        }));
      setAllCertificates(certs);
      if (certs.length > 0) {
        setCertificateDetails(certs[0]);
        setSelectedIndex(0);
      }
    } catch (error) {
      // console.log(error);
    } finally {
      setPageLoader(false);
    }
  }, [router.query, isCareerPath, setCertificateDetails]);

  // Fetch certificates when query changes
  useEffect(() => {
    if (router.query.id) {
      fetchAllCertificates();
    } else {
      // No certificate ID in query, stop loading
      setPageLoader(false);
    }
  }, [router.query, fetchAllCertificates]);

  // Update selected certificate when index changes
  useEffect(() => {
    if (allCertificates.length > 0 && allCertificates[selectedIndex]) {
      const newCert = allCertificates[selectedIndex];
      if (newCert?.certificate?.id) {
        setIsCertLoading(true);
        setCertificateDetails(newCert);
      }
    }
  }, [selectedIndex, allCertificates, setCertificateDetails]);

  // Handle certificate image generation and share URL
  useEffect(() => {
    const certificateDetails = allCertificates[selectedIndex];
    if (certificateDetails?.certificate?._id) {
      const pdfLink = getCertificatePdfUrl(certificateDetails.certificate.id, isCareerPath);
      setShareUrl(pdfLink);

      const handleContextMenu = (e: Event) => {
        e.preventDefault();
      };
      document.addEventListener('contextmenu', handleContextMenu);

      const loadingTask = pdfjs.getDocument(pdfLink);
      loadingTask.promise.then((pdf) => {
        pdf.getPage(1).then((page) => {
          const scale = 1;
          const viewport = page.getViewport({ scale });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          const renderContext: any = {
            canvasContext: context,
            viewport,
          };

          const task = page?.render(renderContext);
          task.promise.then(() => {
            const imageLink = document.getElementById('download-img');
            const downloadImage = canvas.toDataURL('image/png');
            setCertificateImage(downloadImage);
            imageLink?.setAttribute('href', downloadImage);
            imageLink?.setAttribute('download', `${certificateDetails?.certificate?.id}.png`);
            hidePdfLayers();
          });
        });
      });

      // Cleanup: remove context menu listener
      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
      };
    }
    return undefined;
  }, [allCertificates, selectedIndex, isCareerPath]);

  return {
    allCertificates,
    selectedIndex,
    setSelectedIndex,
    isCertLoading,
    setIsCertLoading,
    pageLoader,
    shareUrl,
    certificateImage,
  };
};

export default useCertificates;
